import { useEffect, useState } from "react";
import { TourTarget, tourSelector } from "./tourTargets";

/**
 * Resolving `data-tour` anchors for coach marks.
 *
 * Two traps this deliberately avoids:
 *  - `offsetParent` is null for `position: fixed` elements, so it can't
 *    be used as a visibility test — the canvas surface is fixed.
 *  - The canvas isn't hidden with `display: none` when the reader is
 *    showing; it's parked at -10000px (see ArgStudio.css), so an
 *    on-paper "visible" element can still be nowhere near the screen.
 */
export function findVisibleAnchor(target: TourTarget): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(tourSelector(target));
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  const style = getComputedStyle(el);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  ) {
    return null;
  }

  // Entirely outside the viewport — the parked-canvas case.
  if (
    rect.bottom <= 0 ||
    rect.right <= 0 ||
    rect.top >= window.innerHeight ||
    rect.left >= window.innerWidth
  ) {
    return null;
  }

  return el;
}

const POLL_MS = 400;

/**
 * Track which of the given targets are currently on screen. Polled
 * rather than ref-based because these anchors live in components whose
 * mount order we don't control (the highlight tip, the canvas surface),
 * and they come and go as the user works.
 */
export function useVisibleAnchors(
  targets: TourTarget[]
): Partial<Record<TourTarget, HTMLElement>> {
  const key = targets.join("|");
  const [anchors, setAnchors] = useState<Partial<Record<TourTarget, HTMLElement>>>(
    {}
  );

  useEffect(() => {
    const list = key ? (key.split("|") as TourTarget[]) : [];
    const scan = () => {
      setAnchors((previous) => {
        const next: Partial<Record<TourTarget, HTMLElement>> = {};
        let changed = false;
        for (const target of list) {
          const el = findVisibleAnchor(target);
          if (el) next[target] = el;
          if (previous[target] !== el && !(previous[target] == null && el == null)) {
            changed = true;
          }
        }
        if (!changed && Object.keys(previous).length === Object.keys(next).length) {
          return previous;
        }
        return next;
      });
    };
    scan();
    const timer = setInterval(scan, POLL_MS);
    return () => clearInterval(timer);
  }, [key]);

  return anchors;
}
